import { useEffect, useState, type ReactNode } from 'react';
import { Column, DataGrid } from './DataGrid';

const PAGE = 50;

interface Props<T extends Record<string, unknown>> {
  title: string;
  columns: Column<T>[];
  useData: (grouping?: string, search?: string) => {
    data?: T[];
    isFetching: boolean;
  };
  rowAction?: (row: T) => ReactNode;
  headerExtra?: ReactNode;
}

/**
 * Name-search grid. SS11: the "Client starts with" letter filter was removed — the
 * list shows the first ~50 rows with a "See More" to reveal more (up to the 500 the
 * API returns), and users narrow with Search. Rendering only the visible slice keeps
 * the screen fast even though the query returns up to 500 rows.
 */
export function GroupedGrid<T extends Record<string, unknown>>({
  title,
  columns,
  useData,
  rowAction,
  headerExtra,
}: Props<T>) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(PAGE);
  const { data, isFetching } = useData(undefined, search || undefined);
  const rows = data ?? [];
  const shown = rows.slice(0, visible);

  // New result set -> reset to the first page.
  useEffect(() => { setVisible(PAGE); }, [search]);

  return (
    // Fill the viewport height so the grid gets a bounded, internally-scrolling area
    // (its horizontal scrollbar stays on screen instead of at the bottom of the list).
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex flex-shrink-0 flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="label">Search (Lookup Client Name)</label>
          <div className="flex gap-2">
            <input
              className="input"
              placeholder="Client name…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setSearch(searchInput)}
            />
            <button className="btn-secondary" onClick={() => setSearch(searchInput)}>
              Search
            </button>
            {search && (
              <button className="btn-secondary" onClick={() => { setSearch(''); setSearchInput(''); }}>
                Clear
              </button>
            )}
          </div>
        </div>
        {headerExtra}
      </div>

      <h2 className="flex-shrink-0 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      {/* The grid is the only scrolling area (no second scrollbar around it). */}
      <div className="min-h-0 flex-1">
        <DataGrid columns={columns} rows={shown} loading={isFetching} rowAction={rowAction} />
      </div>
      <div className="flex flex-shrink-0 items-center gap-3 text-xs text-muted-foreground">
        <span>Showing {shown.length} of {rows.length}{rows.length >= 500 ? '+ (first 500)' : ''}. Use Search to find a specific client.</span>
        {rows.length > visible && (
          <button className="btn-secondary !py-1 text-xs" onClick={() => setVisible((v) => v + PAGE)}>See More</button>
        )}
      </div>
    </div>
  );
}
